import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Bell, Plus, Trash2, Play, Pause, Edit2, Check, X, Loader2,
  TrendingDown, TrendingUp, DollarSign, MousePointer, Eye, Target,
  Facebook, Chrome, Music, BarChart3, Search, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METRICS = [
  { id: 'roi', name: 'ROI', icon: TrendingUp, unit: '%', description: 'Retorno de inversión' },
  { id: 'ctr', name: 'CTR', icon: MousePointer, unit: '%', description: 'Click-through rate' },
  { id: 'spent', name: 'Gasto', icon: DollarSign, unit: '$', description: 'Gasto total' },
  { id: 'conversions', name: 'Conversiones', icon: Target, unit: '', description: 'Total conversiones' },
  { id: 'clicks', name: 'Clicks', icon: MousePointer, unit: '', description: 'Total clicks' },
  { id: 'impressions', name: 'Impresiones', icon: Eye, unit: '', description: 'Total impresiones' }
];

const OPERATORS = [
  { id: 'lt', name: 'Menor que', symbol: '<', description: 'Alertar cuando el valor baje' },
  { id: 'gt', name: 'Mayor que', symbol: '>', description: 'Alertar cuando el valor suba' },
  { id: 'eq', name: 'Igual a', symbol: '=', description: 'Alertar cuando alcance el valor' }
];

const PLATFORMS = [
  { id: 'all', name: 'Todas', icon: BarChart3, color: '#d946ef' },
  { id: 'meta', name: 'Meta Ads', icon: Facebook, color: '#0668E1' },
  { id: 'google', name: 'Google Ads', icon: Chrome, color: '#EA4335' },
  { id: 'tiktok', name: 'TikTok', icon: Music, color: '#000000' },
  { id: 'ga4', name: 'GA4', icon: BarChart3, color: '#F9AB00' },
  { id: 'gsc', name: 'GSC', icon: Search, color: '#4285F4' }
];

export default function Alerts() {
  const { token, user } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    metric: 'roi',
    operator: 'lt',
    threshold: 0,
    platform: 'all',
    client_id: '',
    recipient_email: user?.email || '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.email && !formData.recipient_email) {
      setFormData(prev => ({ ...prev, recipient_email: user.email }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [alertsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/alerts${clientId ? `?client_id=${clientId}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAlerts(Array.isArray(alertsRes.data) ? alertsRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      metric: 'roi',
      operator: 'lt',
      threshold: 0,
      platform: 'all',
      client_id: '',
      recipient_email: user?.email || '',
      is_active: true
    });
    setEditingAlert(null);
  };

  const openModal = (alert = null) => {
    if (alert) {
      setEditingAlert(alert);
      setFormData({
        name: alert.name,
        metric: alert.metric,
        operator: alert.operator,
        threshold: alert.threshold,
        platform: alert.platform || 'all',
        client_id: alert.client_id || '',
        recipient_email: alert.recipient_email,
        is_active: alert.is_active
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Ingresa un nombre para la alerta');
      return;
    }

    const payload = {
      ...formData,
      client_id: formData.client_id || null,
      threshold: parseFloat(formData.threshold)
    };

    try {
      if (editingAlert) {
        await axios.put(`${API}/alerts/${editingAlert.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Alerta actualizada');
      } else {
        await axios.post(`${API}/alerts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Alerta creada');
      }
      closeModal();
      fetchData();
    } catch (error) {
      toast.error('Error al guardar la alerta');
    }
  };

  const toggleActive = async (alert) => {
    try {
      await axios.put(`${API}/alerts/${alert.id}`, { is_active: !alert.is_active }, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(alert.is_active ? 'Alerta pausada' : 'Alerta activada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar la alerta');
    }
  };

  const deleteAlert = async (alertId) => {
    if (!window.confirm('¿Eliminar esta alerta?')) return;
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Alerta eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la alerta');
    }
  };

  const checkAlert = async (alertId) => {
    setCheckingId(alertId);
    try {
      const response = await axios.post(`${API}/alerts/${alertId}/check`, {}, 
        { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.triggered) {
        toast.warning(`⚠️ ${response.data.message}`);
      } else {
        toast.success(`✓ ${response.data.message}`);
      }
      fetchData();
    } catch (error) {
      toast.error('Error al verificar la alerta');
    } finally {
      setCheckingId(null);
    }
  };

  const checkAllAlerts = async () => {
    setCheckingAll(true);
    try {
      const response = await axios.post(`${API}/alerts/check-all`, {}, 
        { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.triggered > 0) {
        toast.warning(`⚠️ ${response.data.triggered} alerta(s) activada(s)`);
      } else {
        toast.success(`✓ ${response.data.total_checked} alertas verificadas, ninguna activada`);
      }
      fetchData();
    } catch (error) {
      toast.error('Error al verificar alertas');
    } finally {
      setCheckingAll(false);
    }
  };

  const getMetricIcon = (metricId) => {
    const metric = METRICS.find(m => m.id === metricId);
    return metric?.icon || TrendingUp;
  };

  const formatThreshold = (metric, value) => {
    const m = METRICS.find(x => x.id === metric);
    if (m?.unit === '$') return `$${value.toLocaleString()}`;
    if (m?.unit === '%') return `${value}%`;
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ai-glow" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="alerts-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl mb-2">Alertas Inteligentes</h1>
            <p className="text-secondary">Recibe notificaciones cuando tus KPIs crucen umbrales importantes</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {alerts.length > 0 && (
              <Button variant="outline" onClick={checkAllAlerts} disabled={checkingAll}
                className="rounded-full border-white/20">
                {checkingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Verificar Todas
              </Button>
            )}
            <Button onClick={() => openModal()}
              className="bg-gradient-to-r from-ai-glow to-brand-meta text-white rounded-full h-12 px-6">
              <Plus className="w-5 h-5 mr-2" />
              Nueva Alerta
            </Button>
          </div>
        </motion.div>

        {/* Alerts List */}
        {alerts.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-secondary" />
            <h3 className="text-xl font-medium mb-2">No hay alertas configuradas</h3>
            <p className="text-secondary mb-6">Crea tu primera alerta para monitorear tus KPIs</p>
            <Button onClick={() => openModal()} className="bg-ai-glow text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Crear Alerta
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {alerts.map((alert, index) => {
              const MetricIcon = getMetricIcon(alert.metric);
              const platform = PLATFORMS.find(p => p.id === alert.platform);
              const operator = OPERATORS.find(o => o.id === alert.operator);
              
              return (
                <motion.div key={alert.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-card p-6 ${!alert.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-xl bg-warning/20">
                        <AlertTriangle className="w-6 h-6 text-warning" />
                      </div>
                      
                      <div>
                        <h3 className="font-medium text-lg">{alert.name}</h3>
                        <div className="flex items-center space-x-3 mt-1 text-sm">
                          <span className="flex items-center text-secondary">
                            <MetricIcon className="w-4 h-4 mr-1" />
                            {METRICS.find(m => m.id === alert.metric)?.name}
                          </span>
                          <span className="text-ai-glow font-medium">
                            {operator?.symbol} {formatThreshold(alert.metric, alert.threshold)}
                          </span>
                          {platform && platform.id !== 'all' && (
                            <span className="flex items-center" style={{ color: platform.color }}>
                              <platform.icon className="w-4 h-4 mr-1" />
                              {platform.name}
                            </span>
                          )}
                        </div>
                        {alert.trigger_count > 0 && (
                          <p className="text-xs text-secondary mt-2">
                            Activada {alert.trigger_count} vez(ces) • Última: {new Date(alert.last_triggered_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="icon" onClick={() => checkAlert(alert.id)}
                        disabled={checkingId === alert.id} className="hover:bg-success/20 hover:text-success">
                        {checkingId === alert.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
                      </Button>
                      
                      <Button variant="ghost" size="icon" onClick={() => toggleActive(alert)}
                        className={alert.is_active ? 'hover:bg-warning/20 hover:text-warning' : 'hover:bg-success/20 hover:text-success'}>
                        {alert.is_active ? <Pause className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                      </Button>
                      
                      <Button variant="ghost" size="icon" onClick={() => openModal(alert)}
                        className="hover:bg-ai-glow/20 hover:text-ai-glow">
                        <Edit2 className="w-5 h-5" />
                      </Button>
                      
                      <Button variant="ghost" size="icon" onClick={() => deleteAlert(alert.id)}
                        className="hover:bg-error/20 hover:text-error">
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-xl">
                  {editingAlert ? 'Editar Alerta' : 'Nueva Alerta'}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">Nombre de la alerta</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: ROI bajo en Meta Ads"
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white" />
                </div>

                {/* Metric */}
                <div>
                  <label className="block text-sm font-medium mb-2">Métrica a monitorear</label>
                  <div className="grid grid-cols-3 gap-2">
                    {METRICS.map((metric) => {
                      const Icon = metric.icon;
                      return (
                        <button key={metric.id} type="button"
                          onClick={() => setFormData({ ...formData, metric: metric.id })}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            formData.metric === metric.id
                              ? 'border-ai-glow bg-ai-glow/20'
                              : 'border-white/10 hover:border-white/30'
                          }`}>
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-xs">{metric.name}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Operator & Threshold */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Condición</label>
                    <select value={formData.operator}
                      onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                      className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white">
                      {OPERATORS.map(op => (
                        <option key={op.id} value={op.id}>{op.name} ({op.symbol})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Umbral</label>
                    <input type="number" step="0.01" value={formData.threshold}
                      onChange={(e) => setFormData({ ...formData, threshold: e.target.value })}
                      className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white" />
                  </div>
                </div>

                {/* Platform */}
                <div>
                  <label className="block text-sm font-medium mb-2">Plataforma</label>
                  <select value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white">
                    {PLATFORMS.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Client */}
                <div>
                  <label className="block text-sm font-medium mb-2">Cliente (opcional)</label>
                  <select value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white">
                    <option value="">Todos los clientes</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2">Email para notificaciones</label>
                  <input type="email" value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-lg p-3 text-gray-900 dark:text-white" />
                </div>

                {/* Active */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-sm">Alerta activa</span>
                  <button type="button"
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                    className={`w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-success' : 'bg-white/20'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal} className="rounded-full">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-ai-glow text-white rounded-full">
                    <Check className="w-4 h-4 mr-2" />
                    {editingAlert ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
